import React from 'react';

// Styles
import styles from './info.module.css'

interface Image {
  url: string;
  title?: string | null;
}

interface BaseItem {
  id: number;
  name: string;
  code?: string;
  htmlColor?: string;
  thumbnailImage?: Image;
  image?: Image;
  instructions?: string | null;
}

interface SizeStandard extends BaseItem {
  width?: string | number;
  height?: string | number;
  scaleWidth?: boolean;
  scaleHeight?: boolean;
}

interface EmblemDataProps {
  wizardData: any;
}

const formatKey = (key: string): string => {
  return key
    .split(/(?=[A-Z])/)
    .join(' ')
    .replace(/^./, str => str.toUpperCase());
};

const EmblemWizardDetails: React.FC<EmblemDataProps> = ({
  wizardData
}) => {
  const getImageUrl = (item: BaseItem): string | undefined => {
    return item.thumbnailImage?.url || item.image?.url;
  };

  const renderItem = (key: string, item: BaseItem | { items: Array<any> } | { imageId: string; imageSrc: string }) => {
    if (key === 'designRequest' || key === 'DesignType') return null;

    let name = '';
    let htmlColor = '';
    let imageUrl = '';
    let instructions = '';

    if ('name' in item) {
      name = item.name;
      htmlColor = (item as BaseItem).htmlColor || '';
      imageUrl = getImageUrl(item as BaseItem) ? `${IMAGE_SERVER}${(item as BaseItem).thumbnailImage?.url || (item as BaseItem).image?.url}` : '';
      instructions = (item as BaseItem).instructions || '';
    } else if ('items' in item && (item as { items: Array<any> }).items.length > 0) {
      name = (item as { items: Array<any> }).items[0].option.name;
      htmlColor = (item as { items: Array<any> }).items[0].option.htmlColor || '';
      imageUrl = getImageUrl((item as { items: Array<any> }).items[0].option) ? `${IMAGE_SERVER}${(item as { items: Array<any> }).items[0].option.thumbnailImage?.url || (item as { items: Array<any> }).items[0].option.image?.url}` : '';
      instructions = (item as { items: Array<any> }).items[0].option.instructions || '';
    } else if ('imageId' in item) {
      name = 'Design Type';
      imageUrl = (item as { imageSrc: string }).imageSrc;
    }

    if (key === 'SizeStandard' && 'width' in item) {
      const sizeStandard = item as SizeStandard;
      name = [
        sizeStandard.code ? `${sizeStandard.name} - ` : null,
        sizeStandard.width ? `Width: ${sizeStandard.width}"` : null,
        sizeStandard.height ? ` Height: ${sizeStandard.height}"` : null,
        sizeStandard.scaleHeight ? " Height: Scale Height" : null,
        sizeStandard.scaleWidth ? " Width: Scale Width" : null
      ]
        .filter(Boolean)
        .join('');
    }

    return (
      <div className={styles.lnRow}>
        <span className={styles.name}>{formatKey(key)}:</span>
        <div className='flex flex-row items-center'>
          <div className={styles.detailItem}>
            <span className={styles.value}>{name}</span>
          </div>
          {htmlColor && !imageUrl && (
            <div
              className={styles.colorPreview}
              style={{
                backgroundColor: htmlColor,
                width: '25px',
                height: '25px',
                display: 'inline-block',
                marginLeft: '8px',
                marginRight: '8px',
                backgroundSize: '100%',
              }}
              title={
                htmlColor
                  ? `Color preview: ${htmlColor}`
                  : 'Image preview from background image'
              }
            />
          )}
          {imageUrl && (
            <div
              className={styles.colorPreview}
              style={{
                backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
                width: '25px',
                height: '25px',
                display: 'inline-block',
                marginLeft: '8px',
                marginRight: '8px',
                backgroundSize: '100%',
              }}
            />
          )}
        </div>
        {instructions && <span className={styles.value}> - {instructions}</span>}
      </div>
    );
  };

  return (
    <div className={[styles.card, styles.lnCard, 'w-full'].join(' ')}>
      <h3 className="uppercase text-[#F36E29] font-bold pt-[1.5em] pb-0 px-[1.5em]">Design Details</h3>
      <div className={styles.cardBody}>
        {Object.entries(wizardData)?.map(([key, value]) => renderItem(key, value as any))}
      </div>
    </div>
  );
};

const IMAGE_SERVER = 'https://shop.worldemblem.com/';

export default EmblemWizardDetails;